import React, { createContext, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import { lovable } from "@/integrations/lovable";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signInWithApple: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let bootFinished = false;

    const clearAuthHash = () => {
      const cleanUrl = `${window.location.pathname}${window.location.search}`;
      window.history.replaceState({}, document.title, cleanUrl);
    };

    const applySession = (nextSession: Session | null) => {
      if (!isMounted) return;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      if (nextSession?.user) {
        setSentryUser({ id: nextSession.user.id, email: nextSession.user.email });
      } else {
        clearSentryUser();
      }
    };

    const finishBoot = (nextSession: Session | null) => {
      if (bootFinished || !isMounted) return;
      bootFinished = true;
      applySession(nextSession);
      setLoading(false);
    };

    // Run post-login side effects (last_login, paused/suspended check) without blocking boot
    const runPostLoginEffects = async (currentSession: Session) => {
      try {
        // Update last_login (fire-and-forget)
        supabase
          .from("user_profiles")
          .update({ last_login: new Date().toISOString() } as any)
          .eq("user_id", currentSession.user.id)
          .then(() => {});

        // Check if user is paused or suspended
        const { data: profile } = await supabase
          .from("user_profiles")
          .select("status")
          .eq("user_id", currentSession.user.id)
          .maybeSingle();

        const profileStatus = profile?.status as string | undefined;
        if (profileStatus === "paused" || profileStatus === "suspended") {
          await supabase.auth.signOut();
          const msg = profileStatus === "paused"
            ? "Your account is temporarily paused. Please contact your administrator."
            : "Your account has been suspended. Please contact your administrator.";
          const { toast } = await import("sonner");
          toast.error(msg);
        }
      } catch (err) {
        console.error("Failed to check account status:", err);
      }
    };

    // Last-resort timeout — only fires if neither listener nor getSession resolves
    const bootTimeout = window.setTimeout(() => {
      console.warn("Auth boot timed out; falling back to signed-out state.");
      finishBoot(null);
    }, 5000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
      if (event === "INITIAL_SESSION") {
        window.clearTimeout(bootTimeout);
        finishBoot(currentSession);
        // Run side effects after boot is settled
        if (currentSession?.user) {
          runPostLoginEffects(currentSession);
        }
        return;
      }

      if (event === "SIGNED_IN" && currentSession?.user) {
        // If boot already finished, this is a fresh login (not a restore)
        if (bootFinished) {
          runPostLoginEffects(currentSession);
        }
      }

      // For non-INITIAL_SESSION events during boot, let them finish boot
      window.clearTimeout(bootTimeout);
      if (!bootFinished) {
        finishBoot(currentSession);
      } else {
        applySession(currentSession);
      }
    });

    const restoreSession = async () => {
      const hash = window.location.hash.startsWith("#")
        ? window.location.hash.slice(1)
        : window.location.hash;

      const hashParams = new URLSearchParams(hash);
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");

      if (accessToken && refreshToken) {
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) throw error;

          clearAuthHash();
          if (data.session) {
            window.clearTimeout(bootTimeout);
            finishBoot(data.session);
            runPostLoginEffects(data.session);
            return;
          }
        } catch (error) {
          console.error("Failed to recover auth session from callback hash:", error);
        }
      }

      // Fallback for older SDK versions where INITIAL_SESSION may not fire.
      // Only finalize boot if we got a REAL session. If null, keep waiting
      // for the auth listener or timeout.
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (s) {
          window.clearTimeout(bootTimeout);
          finishBoot(s);
          runPostLoginEffects(s);
        }
        // If s is null, do NOT finishBoot — wait for INITIAL_SESSION or timeout
      } catch (error) {
        console.error("Failed to restore auth session:", error);
        // On error, don't finalize either — let timeout handle it
      }
    };

    restoreSession();

    return () => {
      isMounted = false;
      window.clearTimeout(bootTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    return { error: error as Error | null };
  };

  // Use Lovable managed OAuth for everything except localhost (dev).
  // This covers *.lovable.app, *.lovable.dev, and custom domains like cmdxcenter.com.
  const host = window.location.hostname;
  const isLocalDev = host === "localhost" || host === "127.0.0.1";
  const isLovableHosted = !isLocalDev;

  const signInWithGoogle = async () => {
    if (isLovableHosted) {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      return { error: result.error ? (result.error as Error) : null };
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    return { error: error as Error | null };
  };

  const signInWithApple = async () => {
    if (isLovableHosted) {
      const result = await lovable.auth.signInWithOAuth("apple", {
        redirect_uri: window.location.origin,
      });
      return { error: result.error ? (result.error as Error) : null };
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: { redirectTo: window.location.origin },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    clearSentryUser();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signInWithGoogle, signInWithApple, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
